import React from 'react';
import { X } from 'lucide-react';
import LscInstallVisualSteps, {
  type LscInstallVisualPlatform,
} from './LscInstallVisualSteps';

interface LscInstallInstructionsModalProps {
  open: boolean;
  onClose: () => void;
  platform?: LscInstallVisualPlatform;
}

const TEXT_STEPS: Record<LscInstallVisualPlatform, string[]> = {
  ios: [
    'Open this page in Safari',
    'Tap the Share button',
    'Tap Add to Home Screen',
    'Confirm the name Stream Center',
  ],
  android: [
    'Tap Install App on this page',
    'Confirm the browser install prompt',
    'Open Stream Center from your home screen',
  ],
  generic: [
    'Add Stream Center to your home screen or app list',
    'Open from your phone like an app',
    'Jump back into your league anytime',
  ],
};

const LscInstallInstructionsModal: React.FC<LscInstallInstructionsModalProps> = ({
  open,
  onClose,
  platform = 'ios',
}) => {
  if (!open) return null;

  const steps = TEXT_STEPS[platform];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lsc-install-instructions-title"
      data-testid="lsc-install-instructions-modal"
    >
      <div className="w-full max-w-md max-h-[min(90vh,720px)] overflow-y-auto rounded-xl border border-secondary-700 bg-secondary-900 p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 id="lsc-install-instructions-title" className="text-lg font-semibold text-white">
              Add Stream Center to your phone
            </h2>
            <p className="mt-1 text-sm text-secondary-400">Open your league like an app.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-secondary-400 hover:text-white shrink-0"
            aria-label="Close install instructions"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <LscInstallVisualSteps platform={platform} density="full" className="mb-4" />

        <div className="rounded-lg border border-secondary-700/80 bg-secondary-950/50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-secondary-500 mb-2">
            Quick steps
          </p>
          <ol className="space-y-2 text-sm text-secondary-300 list-decimal list-inside">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default LscInstallInstructionsModal;
